// SPDX-License-Identifier: Apache-2.0

// UYAP `.udf` → markdown. Ported verbatim (with comments preserved) from the
// old Tether impl at `apps/web/src/components/vault/vault-ingest.ts`. This
// is the format used by Turkish courts (UYAP system) and is the on-ramp for
// DavaKasası users.
//
// Format: a plain ZIP archive containing `content.xml`, with the body stored
// as RTF inside a `<content>` element (sometimes CDATA-wrapped). Metadata and
// signature blobs are ignored — we extract the legal-doc *text* only.
//
// RTF→text strategy: strip groups/control-words, decode unicode escapes
// (`\u####` with optional fallback char), preserve paragraph breaks (`\par`,
// `\line`). Hex escapes (`\'XX`) decode as Windows-1254 (UYAP's legacy
// default).
//
// Source heritage note from the old repo (carried forward): reimplemented from
// the public format spec (dev.to writeup by ox3adie1) — no code borrowed from
// closed / license-unclear OSS parsers. Signature verification is out of scope
// for v1; we parse the content, ignore the cryptographic envelope.

import {
  asParagraphs,
  baseTitle,
  corruptResult,
  warnIfLarge,
  wrapWithTitle,
  type IngestResult,
} from './common';

interface JszipEntry {
  async(kind: 'string'): Promise<string>;
}
interface JszipArchive {
  file(name: string): JszipEntry | null;
}
interface JszipModule {
  loadAsync(data: ArrayBuffer): Promise<JszipArchive>;
}

function decodeRtfUnicode(n: number): string {
  if (n < 0) n += 65536;
  try {
    return String.fromCodePoint(n);
  } catch {
    return '';
  }
}

// Windows-1254 (Turkish) — positions 0x80–0xFF that differ from Latin-1.
// Everything else maps 1:1 to Unicode, so we only table the deltas.
const CP1254_MAP: Record<number, number> = {
  0x80: 0x20AC, 0x82: 0x201A, 0x83: 0x0192, 0x84: 0x201E, 0x85: 0x2026,
  0x86: 0x2020, 0x87: 0x2021, 0x88: 0x02C6, 0x89: 0x2030, 0x8A: 0x0160,
  0x8B: 0x2039, 0x8C: 0x0152, 0x91: 0x2018, 0x92: 0x2019, 0x93: 0x201C,
  0x94: 0x201D, 0x95: 0x2022, 0x96: 0x2013, 0x97: 0x2014, 0x98: 0x02DC,
  0x99: 0x2122, 0x9A: 0x0161, 0x9B: 0x203A, 0x9C: 0x0153, 0x9F: 0x0178,
  0xD0: 0x011E, 0xDD: 0x0130, 0xDE: 0x015E, 0xF0: 0x011F, 0xFD: 0x0131,
  0xFE: 0x015F,
};

function decodeCp1254Byte(b: number): string {
  if (b < 0x80) return String.fromCharCode(b);
  const mapped = CP1254_MAP[b];
  return mapped ? String.fromCodePoint(mapped) : String.fromCharCode(b);
}

/** Minimal RTF → plain-text extractor. Strips control-words & groups, keeps
 *  text runs, honours unicode/hex escapes, emits newlines for `\par`/`\line`. */
export function rtfToText(rtf: string): string {
  let out = '';
  let i = 0;
  const n = rtf.length;
  let depth = 0;
  // Skip content of destinations that aren't visible text: fonttbl, colortbl,
  // stylesheet, info, pict, etc. Track the enclosing group depth when we enter.
  let skipDepth = -1;

  while (i < n) {
    const c = rtf[i];

    if (c === '{') {
      depth++;
      i++;
      continue;
    }
    if (c === '}') {
      if (skipDepth >= 0 && depth <= skipDepth) skipDepth = -1;
      depth--;
      i++;
      continue;
    }
    if (c === '\\') {
      const next = rtf[i + 1];
      if (next === '\\' || next === '{' || next === '}') {
        if (skipDepth < 0 && next !== undefined) out += next;
        i += 2;
        continue;
      }
      if (next === '*') {
        i += 2;
        continue;
      }
      if (next === "'") {
        const hex = rtf.slice(i + 2, i + 4);
        if (/^[0-9a-fA-F]{2}$/.test(hex)) {
          if (skipDepth < 0) out += decodeCp1254Byte(parseInt(hex, 16));
          i += 4;
          continue;
        }
        i += 2;
        continue;
      }
      const m = rtf.slice(i + 1).match(/^([a-zA-Z]+)(-?\d+)?(\s?)/);
      if (m) {
        const word = m[1] ?? '';
        const arg = m[2];
        const delim = m[3];
        const hadSpace = delim === ' ';
        const consumed = 1 + m[0].length;

        if (skipDepth < 0) {
          if (word === 'par' || word === 'line' || word === 'sect') {
            out += '\n';
            if (hadSpace) out += ' ';
          } else if (word === 'tab') {
            out += '\t';
          } else if (word === 'u' && arg !== undefined) {
            out += decodeRtfUnicode(parseInt(arg, 10));
            if (rtf[i + consumed] === '?') i++;
          } else if (
            word === 'fonttbl' || word === 'colortbl' ||
            word === 'stylesheet' || word === 'info' ||
            word === 'pict' || word === 'filetbl' ||
            word === 'themedata' || word === 'listtable' ||
            word === 'revtbl' || word === 'generator'
          ) {
            skipDepth = depth;
          } else if (hadSpace && out.length > 0 && !/\s$/.test(out)) {
            out += ' ';
          }
        }
        i += consumed;
        continue;
      }
      i++;
      continue;
    }

    if (skipDepth < 0) {
      if (c === '\n' || c === '\r') out += ' ';
      else if (c !== undefined) out += c;
    }
    i++;
  }

  return out;
}

export async function parseUdf(file: File): Promise<IngestResult> {
  warnIfLarge(file, 'udf');
  const title = baseTitle(file.name);
  try {
    const mod = await import('jszip');
    const JSZip = (mod as unknown as { default?: JszipModule }).default ??
      (mod as unknown as JszipModule);
    const buffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buffer);
    const contentEntry = zip.file('content.xml');
    if (!contentEntry) {
      return corruptResult(
        file.name,
        'UDF: content.xml missing — not a valid UYAP document',
      );
    }
    const xmlString = await contentEntry.async('string');

    // Pull the RTF payload out of <content>…</content>.
    const contentMatch = xmlString.match(/<content[^>]*>([\s\S]*?)<\/content>/i);
    let rtf = contentMatch ? contentMatch[1] ?? '' : '';
    rtf = rtf.replace(/^\s*<!\[CDATA\[/, '').replace(/\]\]>\s*$/, '');
    rtf = rtf
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&amp;/g, '&');

    const text = rtfToText(rtf);
    return {
      title,
      content: wrapWithTitle(title, asParagraphs(text)),
      front: {},
      kind: 'udf',
    };
  } catch (err) {
    return corruptResult(file.name, err instanceof Error ? err.message : String(err));
  }
}
